import type { FieldMetadata, FieldValue, RecordEvent } from '../../../data/client';

/**
 * THE ACTIVITY LINES THE CLIENT PAGE READS (redesign register A2, D7, L13; record 15; brief
 * FE-FOUNDATION section 14). Pure, so the sentence is a test.
 *
 * A CORRECTION is an event whose actor is `{ source: 'API', context: 'Data correction' }`
 * (record 15, "The event the Activity builder reads"). It reads, in A2's words, "Onboarding
 * call corrected from 14 Sep to 9 Sep (Fathom recording), 3 Oct": the field's label, the two
 * values (a date as "14 Sep", a relation as its record's label), the reason the write carried
 * and the day of the event. A side that is empty reads as open question Q-FND-13 decided: before
 * empty "<Field> set to <after> (<reason>), <date>", after empty "<Field> cleared, was <before>
 * (<reason>), <date>". An event that is not a correction yields no line, and the page shows it
 * as the existing timeline row.
 */
export const CORRECTION_CONTEXT = 'Data correction';

export function isCorrection(event: RecordEvent): boolean {
  return (
    event.type === 'updated' &&
    event.actor.source === 'API' &&
    event.actor.context === CORRECTION_CONTEXT &&
    event.diff.length > 0
  );
}

/**
 * "14 Sep": day before month, A2's own form. Not `checkpointDateLabel`, whose display locale
 * writes "Sep 14"; A2 quotes the sentence the owner asked for, so the day leads.
 */
export function shortDay(day: string): string | null {
  if (day.length < 10) return null;
  const [year, month, date] = day.slice(0, 10).split('-').map(Number);
  if (year === undefined || month === undefined || date === undefined) return null;
  if (Number.isNaN(year) || Number.isNaN(month) || Number.isNaN(date)) return null;
  /* Built from its parts: en-GB's short September is "Sept" in current ICU, and A2 writes "Sep". */
  const monthName = new Intl.DateTimeFormat('en-US', { month: 'short', timeZone: 'UTC' }).format(
    new Date(Date.UTC(year, month - 1, date)),
  );
  return `${String(date)} ${monthName}`;
}

/** "3 Oct": the event's day in the person's own calendar. */
export function eventDay(at: string): string {
  const date = new Date(at);
  const local = `${String(date.getFullYear())}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  return shortDay(local) ?? at;
}

function isEmpty(value: FieldValue): boolean {
  return value === null || value === undefined || value === '';
}

/**
 * One side of the change as words: a DATE as "14 Sep", a relation as the label the caller
 * resolved (`relationLabels`, record id to label), anything else as its text.
 */
export function correctionValueWords(
  field: FieldMetadata | undefined,
  value: FieldValue,
  relationLabels: Readonly<Record<string, string>> = {},
): string {
  if (typeof value === 'string') {
    if (field?.type === 'DATE' || field?.type === 'DATETIME') {
      return shortDay(value) ?? value;
    }
    if (field?.type === 'RELATION') return relationLabels[value] ?? value;
    return value;
  }
  return String(value);
}

export function correctionSentence(
  event: RecordEvent,
  fields: readonly FieldMetadata[],
  relationLabels: Readonly<Record<string, string>> = {},
): string | null {
  if (!isCorrection(event)) return null;
  const change = event.diff[0];
  if (change === undefined) return null;
  const field = fields.find((entry) => entry.id === change.fieldId);
  const label = field?.label ?? 'A field';
  const reason = event.reason === undefined || event.reason === '' ? '' : ` (${event.reason})`;
  const day = eventDay(event.at);
  const before = correctionValueWords(field, change.before, relationLabels);
  const after = correctionValueWords(field, change.after, relationLabels);
  if (isEmpty(change.before) && isEmpty(change.after)) return null;
  if (isEmpty(change.before)) return `${label} set to ${after}${reason}, ${day}`;
  if (isEmpty(change.after)) return `${label} cleared, was ${before}${reason}, ${day}`;
  return `${label} corrected from ${before} to ${after}${reason}, ${day}`;
}

/** A note as one Activity row: its first line is the title, the rest folds under "More". */
export function noteParts(body: string): { readonly title: string; readonly rest: string | null } {
  const trimmed = body.trim();
  const breakAt = trimmed.indexOf('\n');
  if (breakAt === -1) return { title: trimmed, rest: null };
  const rest = trimmed.slice(breakAt + 1).trim();
  return { title: trimmed.slice(0, breakAt).trim(), rest: rest === '' ? null : rest };
}
